import { ensureRouteData, prefetchRouteData } from '@/app/query-client'
import {
  fundDividendsQueryOptions,
  fundHoldingsQueryOptions,
  fundIncomeStatementQueryOptions,
  fundListQueryOptions,
  fundNavQueryOptions,
  fundSectorBreakdownQueryOptions,
  fundStockCountQueryOptions,
  fundTrendsQueryOptions,
} from '@/features/mutual-funds/api/queries'
import type { RouterContext } from '@/routes/layouts/root'

/** The URL's symbol as the API and every cache key spell it. */
export const fundSymbolOf = (param: string): string => param.trim().toUpperCase()

/**
 * The `/mutual-funds/fund/$symbol` loader. `$symbol` is read uppercased (`fundSymbolOf`), so a
 * hand-typed `niblsf` is the same cache entries as a row's link.
 *
 * MAIN, awaited: the whole directory (one unpaged read of ~70 funds, an hour fresh), which the
 * page finds the fund in — mm-be has no one-fund read. A symbol it lacks is the screen's
 * not-found state, never this route's error.
 *
 * SECONDARY, only for a real navigation: the reads the page's blocks draw from — the stock count
 * (shared with Fund rankings and Analytics), the fund's sector split, its stocks held month by
 * month, the dividend record, its holdings, NAV history and income statement. Each block waits on
 * its own read, so none holds the page up.
 */
export async function loadFund({
  context: { queryClient },
  params,
  preload,
}: {
  context: RouterContext
  params: { symbol: string }
  preload: boolean
}): Promise<void> {
  const symbol = fundSymbolOf(params.symbol)
  if (!preload) {
    prefetchRouteData(queryClient, fundStockCountQueryOptions(null))
    prefetchRouteData(queryClient, fundSectorBreakdownQueryOptions(symbol, null))
    prefetchRouteData(queryClient, fundTrendsQueryOptions({ funds: [symbol] }))
    prefetchRouteData(queryClient, fundDividendsQueryOptions())
    prefetchRouteData(queryClient, fundHoldingsQueryOptions(symbol, null))
    prefetchRouteData(queryClient, fundNavQueryOptions(symbol))
    prefetchRouteData(queryClient, fundIncomeStatementQueryOptions(symbol))
  }
  await ensureRouteData(queryClient, fundListQueryOptions({}))
}
